import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import i18n from "lib/i18n"

const GiftAidModal = ({ open = false, onConfirm, onReject }) => {
  const modalRef = useRef(null)

  /*
   * Sourced from the `useOutsideClick` hook from `tc`'s `/frontend`.
   * Close the modal when clicking outside of it or pressing "ESC".
   */
  useEffect(() => {
    /**
     * We only want to listen for clicks and key presses when the modal is open,
     * otherwise we could uncheck the gift aid checkbox by clicking anywhere or simply pressing
     * "ESC".
     */
    if (!open) return

    const clickListener = (event) => {
      if (!modalRef.current?.contains(event.target)) {
        onReject()
      }
    }
    const escapeKeyListener = (event) => {
      if (event.key === "Escape") {
        onReject()
      }
    }
    document.addEventListener("click", clickListener, true)
    document.addEventListener("keydown", escapeKeyListener, true)
    return () => {
      document.removeEventListener("click", clickListener, true)
      document.removeEventListener("keydown", escapeKeyListener, true)
    }
  }, [onReject, open])

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
      <section
        role="dialog"
        className="relative max-h-screen max-w-md overflow-y-auto border border-gray-50 bg-white p-5 text-center font-sans text-black shadow-lg sm:w-fit sm:min-w-72"
        ref={modalRef}
      >
        <h6 className="font-sans-heading text-xl" id="gift-aid-modal-title">
          {i18n.t("donation.modal.gift_aid.title")}
        </h6>
        <p className="mb-0 pb-0 text-sm">{i18n.t("donation.modal.gift_aid.subtitle")}</p>
        <p className="mb-0 pb-0 text-xs leading-relaxed">
          {i18n.t("donation.modal.gift_aid.body_1")}
        </p>
        <p className="mb-0 pb-0 text-xs leading-relaxed">
          {i18n.t("donation.modal.gift_aid.body_2")}
        </p>
        <p className="mb-0 pb-0 text-xs leading-relaxed">
          {i18n.t("donation.modal.gift_aid.body_3")}
        </p>
        <div className="flex flex-col items-center justify-center gap-4">
          <button
            type="button"
            className="cursor-pointer rounded-xs border-0 bg-blue-600 px-5 py-2 text-sm text-white transition hover:bg-gray-200"
            onClick={onConfirm}
          >
            {i18n.t("donation.modal.gift_aid.button.confirm")}
          </button>
          <button
            type="button"
            className="mb-3 cursor-pointer border-0 bg-white text-sm font-normal"
            onClick={onReject}
          >
            {i18n.t("donation.modal.gift_aid.button.reject")}
          </button>
        </div>
      </section>
    </div>
  )
}

GiftAidModal.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onReject: PropTypes.func,
}
export default GiftAidModal
